import { CreateToastFnReturn, Divider, useToast } from "@chakra-ui/react";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { Item, Menu } from "react-contexify";
import { Coordinate, SpotType } from "~/generated/graphql.types";
import { SpotForRoutingFragment } from "~/routes/_shared/fragments.generated";
import {
  EditingHyperlapseContext,
  EditModeContext,
  validationLatLon,
} from "../..";
import {
  useInsertAfterSpotInputMutation,
  useInsertDestMutation,
  useInsertStartMutation,
  useRemoveMultipleSpotsMutation,
  useRemoveSpotMutation,
  useUpdateSpotMutation,
} from "./index.generated";
import { getNearestCoordinateOrder } from "../utilities/utilities";

export const MapContextMenu = ({
  courseId,
  contextMenuId,
  clickedLocation,
  spotForRouting,
  nextSpotCategory,
  resetLatestDragOperation,
  latestDragOperation,
}: {
  courseId: string;
  contextMenuId: string;
  clickedLocation: Coordinate;
  spotForRouting: SpotForRoutingFragment[];
  nextSpotCategory: SpotType;
  resetLatestDragOperation: () => void;
  latestDragOperation:
    | {
        spotId: string;
        coordinate: Coordinate;
      }
    | undefined;
}) => {
  const { editMode, spotIdForEdit } = useContext(EditModeContext);
  const toast = useToast();

  const spotForEdit = useMemo(() => {
    if (!spotIdForEdit) undefined;
    if (spotForRouting.filter((p) => p.id === spotIdForEdit).length !== 1)
      undefined;
    return spotForRouting.find((p) => p.id === spotIdForEdit);
  }, [spotIdForEdit]);

  const activeAddStartMenu = nextSpotCategory === SpotType.Start;
  const activeAddDestmenu = nextSpotCategory === SpotType.Destination;
  const activeAddSpotBetweenSpotsMenu = nextSpotCategory === SpotType.Waypoint;
  const activeAddSpotAfterSelectedSpotMenu =
    nextSpotCategory === SpotType.Waypoint &&
    spotIdForEdit !== undefined &&
    spotForEdit?.spotType !== SpotType.Destination;

  const activeRemoveSelectedSpotMenu = useMemo(() => {
    if (!spotForEdit?.spotType) return false;
    if (editMode === "routing") {
      if (
        [
          SpotType.Waypoint,
          SpotType.RoutingGuide,
          SpotType.OptionalWaypoint,
        ].includes(spotForEdit?.spotType)
      )
        return true;
    } else {
      if (
        [SpotType.Narration, SpotType.SubaruMoment].includes(
          spotForEdit?.spotType
        )
      )
        return true;
    }
    return false;
  }, [editMode, spotIdForEdit]);

  // const activeAddHyperlapseSpotMenu = editMode === "contents";
  const activeAddHyperlapseSpotMenu = false;

  return (
    <Menu id={contextMenuId}>
      {latestDragOperation && (
        <>
          <UndoDragOperationMenu
            lastestDragOperation={latestDragOperation}
            resetLatestDragOperation={resetLatestDragOperation}
          />
          <Divider height={"1.5px"} bg={"blackAlpha.600"} />
        </>
      )}
      {activeAddStartMenu ? (
        <AddStartSpotMenu
          courseId={courseId}
          clickedLocation={clickedLocation}
          toast={toast}
        />
      ) : null}
      {activeAddDestmenu ? (
        <AddDestSpotMenu
          courseId={courseId}
          clickedLocation={clickedLocation}
          toast={toast}
        />
      ) : null}
      {activeAddSpotBetweenSpotsMenu ? (
        <AddSpotBetweenSpotsMenu
          spotForRouting={spotForRouting}
          clickedLocation={clickedLocation}
          toast={toast}
        />
      ) : null}
      {activeAddSpotAfterSelectedSpotMenu ? (
        <AddSpotAfterSelectedSpotMenu
          spotForRouting={spotForRouting}
          clickedLocation={clickedLocation}
          toast={toast}
        />
      ) : null}
      {activeRemoveSelectedSpotMenu ? (
        <RemoveSelectedSpotMenu
          courseId={courseId}
          spotForRouting={spotForRouting}
        />
      ) : null}
      {activeAddHyperlapseSpotMenu ? (
        <>
          <Divider />
          <AddHyperlapseSpotMenu clickedLocation={clickedLocation} />
        </>
      ) : null}
      <Divider height={"1.5px"} bg={"blackAlpha.600"} />
      <CopyCoordinateMenu clickedLocation={clickedLocation} />
      <OpenStreetViewViewerMenu />
    </Menu>
  );
};

const UndoDragOperationMenu = ({
  lastestDragOperation,
  resetLatestDragOperation,
}: {
  lastestDragOperation: {
    spotId: string;
    coordinate: Coordinate;
  };
  resetLatestDragOperation: () => void;
}) => {
  const toast = useToast();
  const [updateSpotRes, updateSpot] = useUpdateSpotMutation();

  useEffect(() => {
    if (updateSpotRes.data) {
      resetLatestDragOperation();
      toast({
        title: "スポットのドラッグをキャンセルしました。",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [updateSpotRes]);

  const onClickResetDragOperation = useCallback(() => {
    updateSpot(
      {
        input: {
          id: lastestDragOperation.spotId,
          coordinate: lastestDragOperation.coordinate,
        },
      },
      { additionalTypenames: ["Course", "RoutingInfomationPayload"] }
    );
  }, [lastestDragOperation]);

  return (
    <Item onClick={onClickResetDragOperation}>
      スポットのドラッグをキャンセル
    </Item>
  );
};

const CopyCoordinateMenu = ({
  clickedLocation,
}: {
  clickedLocation: Coordinate;
}) => {
  const toast = useToast();
  const copyCoordinate = (coord: Coordinate) => {
    const coordString = `${coord.lat},${coord.lon}`;
    navigator.clipboard.writeText(coordString);
    toast({
      title: "座標をコピーしました。",
      description: `座標をコピーしました。\n${coordString}`,
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  };
  return (
    <Item onClick={() => copyCoordinate(clickedLocation)}>
      緯度経度情報をコピー
    </Item>
  );
};

const OpenStreetViewViewerMenu = () => {
  const { switchStreetViewViewer } = useContext(EditModeContext);
  return (
    <Item onClick={() => switchStreetViewViewer(true)}>StreetViewを閲覧</Item>
  );
};

const AddStartSpotMenu = ({
  courseId,
  clickedLocation,
  toast,
}: {
  courseId: string;
  clickedLocation: Coordinate;
  toast: CreateToastFnReturn;
}) => {
  const [, insertStart] = useInsertStartMutation();

  const onClickInsertStart = useCallback(() => {
    const isValidLocation = validationLatLon({
      lat: clickedLocation.lat,
      lon: clickedLocation.lon,
    });

    if (!isValidLocation.isValid) {
      toast({
        status: "error",
        title: "入力値が不正です。",
        description: isValidLocation.message,
      });
      return;
    }

    insertStart(
      { input: { courseId, coordinate: clickedLocation } },
      {
        additionalTypenames: ["Course", "RoutingInfomationPayload"],
      }
    );
  }, [clickedLocation]);

  return <Item onClick={onClickInsertStart}>スタート地点を登録</Item>;
};

const AddDestSpotMenu = ({
  courseId,
  clickedLocation,
  toast,
}: {
  courseId: string;
  clickedLocation: Coordinate;
  toast: CreateToastFnReturn;
}) => {
  const [, insertDest] = useInsertDestMutation();

  const onClickInsertDest = useCallback(() => {
    const isValidLocation = validationLatLon({
      lat: clickedLocation.lat,
      lon: clickedLocation.lon,
    });

    if (!isValidLocation.isValid) {
      toast({
        status: "error",
        title: "入力値が不正です。",
        description: isValidLocation.message,
      });
      return;
    }

    insertDest(
      { input: { courseId, coordinate: clickedLocation } },
      {
        additionalTypenames: ["Course", "RoutingInfomationPayload"],
      }
    );
  }, [clickedLocation]);

  return <Item onClick={onClickInsertDest}>ゴール地点を登録</Item>;
};

const AddSpotBetweenSpotsMenu = ({
  spotForRouting,
  clickedLocation,
  toast,
}: {
  spotForRouting: SpotForRoutingFragment[];
  clickedLocation: Coordinate;
  toast: CreateToastFnReturn;
}) => {
  const { editMode } = useContext(EditModeContext);

  const bothSideSpots = useMemo(() => {
    const nearestOrder = getNearestCoordinateOrder({
      sourceCoordinate: clickedLocation,
      targetCoordinates: spotForRouting.map((s) => s.coordinate),
    });
    if (nearestOrder === undefined) return null;
    const nearestSpots = {
      prevSpot: spotForRouting[nearestOrder],
      nextSpot: spotForRouting[nearestOrder + 1],
    };
    return nearestSpots;
  }, [clickedLocation, spotForRouting]);

  const [insertAfterSpotRes, insertAfterSpot] =
    useInsertAfterSpotInputMutation();

  useEffect(() => {
    if (!insertAfterSpotRes.data) return;
    if (
      insertAfterSpotRes.data?.insertAfterSpot.__typename ===
      "InsertSpotSuccess"
    ) {
      toast({
        title: "スポットを追加しました。",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (
      insertAfterSpotRes.data?.insertAfterSpot.__typename ===
      "InsertSpotFailure"
    ) {
      console.error(insertAfterSpotRes);
      toast({
        title: "スポットの追加に失敗しました。",
        description: insertAfterSpotRes.data.insertAfterSpot.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
  }, [toast, insertAfterSpotRes.data]);

  const onClickInsertSpot = useCallback(() => {
    if (!bothSideSpots) return;

    const isValidLocation = validationLatLon({
      lat: clickedLocation.lat,
      lon: clickedLocation.lon,
    });

    if (!isValidLocation.isValid) {
      toast({
        status: "error",
        title: "入力値が不正です。",
        description: isValidLocation.message,
      });
      return;
    }

    insertAfterSpot(
      {
        input: {
          targetSpotId: bothSideSpots.prevSpot.id,
          coordinate: clickedLocation,
          courseId: bothSideSpots.prevSpot.courseId,
          spotType:
            editMode === "contents"
              ? SpotType.Narration
              : !!bothSideSpots.prevSpot.parentSpotId
              ? SpotType.RoutingGuide
              : SpotType.Waypoint,
        },
      },
      {
        additionalTypenames: ["Course", "RoutingInfomationPayload"],
      }
    );
  }, [clickedLocation, spotForRouting]);

  const message =
    editMode === "routing" ? "スポットを追加" : "音声スポットを追加";
  return <Item onClick={() => onClickInsertSpot()}>{message}</Item>;
};

const AddSpotAfterSelectedSpotMenu = ({
  spotForRouting,
  clickedLocation,
  toast,
}: {
  spotForRouting: SpotForRoutingFragment[];
  clickedLocation: Coordinate;
  toast: CreateToastFnReturn;
}) => {
  const { editMode, spotIdForEdit } = useContext(EditModeContext);

  const [insertBetweenTwoSpotRes, insertBetweenTwoSpot] =
    useInsertAfterSpotInputMutation();

  useEffect(() => {
    if (!insertBetweenTwoSpotRes.data) return;
    if (
      insertBetweenTwoSpotRes.data?.insertAfterSpot.__typename ===
      "InsertSpotSuccess"
    ) {
      toast({
        title: "スポットを追加しました。",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (
      insertBetweenTwoSpotRes.data?.insertAfterSpot.__typename ===
      "InsertSpotFailure"
    ) {
      console.error(insertBetweenTwoSpotRes);
      toast({
        title: "スポットの追加に失敗しました。",
        description: insertBetweenTwoSpotRes.data.insertAfterSpot.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
  }, [toast, insertBetweenTwoSpotRes.data]);

  const onClickInsertSpotAfter = useCallback(() => {
    const selectedSpot = spotForRouting.find((p) => p.id === spotIdForEdit);
    if (!selectedSpot) return;

    const isValidLocation = validationLatLon({
      lat: clickedLocation.lat,
      lon: clickedLocation.lon,
    });

    if (!isValidLocation.isValid) {
      toast({
        status: "error",
        title: "入力値が不正です。",
        description: isValidLocation.message,
      });
      return;
    }

    insertBetweenTwoSpot(
      {
        input: {
          targetSpotId: selectedSpot.id,
          courseId: selectedSpot.courseId,
          coordinate: clickedLocation,
          spotType:
            editMode === "contents"
              ? SpotType.Narration
              : !!selectedSpot.parentSpotId
              ? SpotType.RoutingGuide
              : SpotType.Waypoint,
        },
      },
      {
        additionalTypenames: ["Course", "RoutingInfomationPayload"],
      }
    );
  }, [spotForRouting, clickedLocation, spotIdForEdit]);
  const message =
    editMode === "routing"
      ? "選択したスポットの後にスポットを追加"
      : "選択したスポットの後に音声スポットを追加";

  return <Item onClick={onClickInsertSpotAfter}>{message}</Item>;
};

const RemoveSelectedSpotMenu = ({
  courseId,
  spotForRouting,
}: {
  courseId: string;
  spotForRouting: SpotForRoutingFragment[];
}) => {
  const { spotIdForEdit, setSpotIdForEdit } = useContext(EditModeContext);

  const [, removeSpot] = useRemoveSpotMutation();
  const [, removeMultipleSpots] = useRemoveMultipleSpotsMutation();

  const onClickRemoveSpot = useCallback(() => {
    // 選択した点の情報を取得。
    if (spotIdForEdit === undefined) return;
    if (spotForRouting.filter((p) => p.id === spotIdForEdit).length !== 1)
      return;
    const editSpot = spotForRouting.filter((p) => p.id === spotIdForEdit)[0];

    if (editSpot.spotType !== SpotType.OptionalWaypoint) {
      // もし、選択している点がOptional Waypointでなかったら自身を削除して終わり。
      removeSpot(
        { input: { id: spotIdForEdit } },
        {
          additionalTypenames: ["Course", "RoutingInfomationPayload"],
        }
      );
    } else {
      // もし、選択している点がOptional Waypointであったら、点を削除する際にはその点をparentSpotIdとして持つ全ての点を削除する。
      if (
        editSpot.parentSpotId === undefined ||
        editSpot.parentSpotId === null
      ) {
        return;
      }
      removeMultipleSpots(
        {
          input: { courseId: courseId, parentSpotId: editSpot.parentSpotId },
        },
        {
          additionalTypenames: ["Course", "RoutingInfomationPayload"],
        }
      );
    }
    setSpotIdForEdit(undefined);
  }, [spotIdForEdit, spotForRouting]);

  return <Item onClick={onClickRemoveSpot}>選択したスポットを削除</Item>;
};

const AddHyperlapseSpotMenu = ({
  clickedLocation,
}: {
  clickedLocation: Coordinate;
}) => {
  const { editingHyperlapseSpots, setEditingHyperlapseSpots } = useContext(
    EditingHyperlapseContext
  );

  const addhyperlapseSpot = useCallback(
    (clickedLocation: Coordinate) => {
      const onClickAddHyperlapseSpotStart = (addCoordinate: Coordinate) => {
        setEditingHyperlapseSpots([addCoordinate]);
      };

      const onClickAddHyperlapseSpotDest = (addCoordinate: Coordinate) => {
        setEditingHyperlapseSpots([...editingHyperlapseSpots, addCoordinate]);
      };

      const onClickAddHyperlapseSpotMid = (addCoordinate: Coordinate) => {
        const nearestSpotOrder = getNearestCoordinateOrder({
          sourceCoordinate: addCoordinate,
          targetCoordinates: editingHyperlapseSpots,
        });
        if (nearestSpotOrder === undefined) return;
        setEditingHyperlapseSpots([
          ...editingHyperlapseSpots.slice(0, nearestSpotOrder + 1),
          addCoordinate,
          ...editingHyperlapseSpots.slice(nearestSpotOrder + 1),
        ]);
      };

      if (!clickedLocation) return;
      if (editingHyperlapseSpots.length === 0) {
        onClickAddHyperlapseSpotStart(clickedLocation);
      } else if (editingHyperlapseSpots.length === 1) {
        onClickAddHyperlapseSpotDest(clickedLocation);
      } else {
        onClickAddHyperlapseSpotMid(clickedLocation);
      }
    },
    [clickedLocation, editingHyperlapseSpots]
  );
  return (
    <Item onClick={() => addhyperlapseSpot(clickedLocation)}>
      Hyperlapse生成用スポットとして登録
    </Item>
  );
};
